import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { FileType } from 'src/models/file';
import { INews, LocalizedText } from '.';

interface IAddNewsProps {
  onSuccess: () => void;
  onClose: () => void;
  news?: INews | null;
}

// Accepted media types for news
const ACCEPTED_MEDIA = [
  FileType.png,
  FileType.jpeg,
  FileType.jpg,
  FileType.gif,
  FileType.svg,
  FileType.pdf,
  FileType.doc,
  FileType.docx,
  FileType.wav,
  FileType.mp3,
  FileType.aac
];

type LanguageTab = 'en' | 'ar' | 'fr';

const AddNews: React.FC<IAddNewsProps> = ({ onSuccess, onClose, news }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isEditMode = !!news;

  const [activeLanguage, setActiveLanguage] = useState<LanguageTab>('en');
  const [title, setTitle] = useState<LocalizedText>({ en: '', ar: '', fr: '' });
  const [content, setContent] = useState<LocalizedText>({ en: '', ar: '', fr: '' });
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string>('');

  useEffect(() => {
    if (!news) {
      setTitle({ en: '', ar: '', fr: '' });
      setContent({ en: '', ar: '', fr: '' });
      setMediaFile(null);
      setImageUrl('');
      setActiveLanguage('en');
      return;
    }

    setTitle({
      en: news.title?.en ?? '',
      ar: news.title?.ar ?? '',
      fr: news.title?.fr ?? ''
    });
    setContent({
      en: news.content?.en ?? '',
      ar: news.content?.ar ?? '',
      fr: news.content?.fr ?? ''
    });
    setMediaFile(null);
    setImageUrl(news.imageUrl ?? '');
    setActiveLanguage('en');
  }, [news]);

  const handleLanguageChange = (_: React.SyntheticEvent, newValue: LanguageTab) => {
    setActiveLanguage(newValue);
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.toLowerCase().split('.').pop() || '';
    if (!Object.values(ACCEPTED_MEDIA).some((t) => t.endsWith(ext))) {
      dispatch(showAlert({ type: 'error', message: 'Unsupported file type.' }));
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      dispatch(showAlert({ type: 'error', message: 'Max file size is 10MB.' }));
      return;
    }
    setMediaFile(f);
  };

  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const lang = activeLanguage;
    const value = e.target.value;
    setTitle((prev) => ({ ...prev, [lang]: value }));
  }, [activeLanguage]);

  const handleContentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const lang = activeLanguage;
    const value = e.target.value;
    setContent((prev) => ({ ...prev, [lang]: value }));
  }, [activeLanguage]);

  const submit = useCallback(async () => {
    if (!title.en.trim() || !title.ar.trim() || !title.fr.trim()) {
      dispatch(showAlert({ type: 'error', message: 'All titles (EN, AR, FR) are required.' }));
      return;
    }
    if (!content.en.trim() || !content.ar.trim() || !content.fr.trim()) {
      dispatch(showAlert({ type: 'error', message: 'All content (EN, AR, FR) is required.' }));
      return;
    }

    const form = new FormData();
    form.append('En_Title', title.en);
    form.append('Ar_Title', title.ar);
    form.append('Fr_Title', title.fr);
    form.append('En_Content', content.en);
    form.append('Ar_Content', content.ar);
    form.append('Fr_Content', content.fr);

    if (mediaFile) form.append('Image', mediaFile);
    else if (isEditMode && imageUrl) form.append('ImageUrl', imageUrl);

    const endpoint = isEditMode ? 'api/News/update' : 'api/News/create';
    if (isEditMode && news?.id) form.append('Id', news.id);

    const res = await DataService.postForm(endpoint, form);
    if (res.ok) {
      onSuccess();
    } else {
      try {
        const r = await res.json();
        dispatch(showAlert({ type: 'error', message: r?.message ?? 'Failed to save news.' }));
      } catch {
        dispatch(showAlert({ type: 'error', message: 'Failed to save news.' }));
      }
    }
  }, [title, content, mediaFile, imageUrl, isEditMode, news?.id, dispatch, onSuccess]);

  const languageConfig = {
    en: { title: 'Title (English)', content: 'Content (English)', dir: 'ltr', align: 'left' },
    ar: { title: 'العنوان (Title)', content: 'المحتوى (Content)', dir: 'rtl', align: 'right' },
    fr: { title: 'Titre (Title)', content: 'Contenu (Content)', dir: 'ltr', align: 'left' }
  };

  const currentLangConfig = languageConfig[activeLanguage];

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      scroll="body"
      PaperProps={{
        sx: {
          borderRadius: 3,
          width: '90vw',
          maxWidth: 1200
        }
      }}
    >
      <DialogTitle
        sx={{
          position: 'relative',
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          py: 1.5,
          pl: 2,
          pr: 2
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {isEditMode ? 'Edit News' : 'Add News'}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.primary.contrastText
          }}
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 3, px: 3 }}>
        {/* Single Tab Section for Language Selection */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2,  mb: 3 }}>
          <Tabs
            value={activeLanguage}
            onChange={handleLanguageChange}
            sx={{
              '& .MuiTab-root': {
                minWidth: 100,
                fontWeight: 600
              }
            }}
          >
            <Tab label="English" value="en" />
            <Tab label="العربية" value="ar" />
            <Tab label="Français" value="fr" />
          </Tabs>
        </Box>

        <Stack spacing={3}>
          {/* Title Input */}
          <TextField
            fullWidth
            label={currentLangConfig.title}
            value={title[activeLanguage]}
            onChange={handleTitleChange}
            inputProps={{ dir: currentLangConfig.dir }}
            sx={{
              '& .MuiInputBase-input': {
                textAlign: currentLangConfig.align as 'left' | 'right'
              }
            }}
          />

          {/* Content Input */}
          <TextField
            fullWidth
            multiline
            minRows={8}
            label={currentLangConfig.content}
            value={content[activeLanguage]}
            onChange={handleContentChange}
            inputProps={{ dir: currentLangConfig.dir }}
            sx={{
              '& .MuiInputBase-input': {
                textAlign: currentLangConfig.align as 'left' | 'right'
              }
            }}
          />

          <Divider />

          {/* Media Upload Section */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Media Upload
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" startIcon={<UploadFileIcon />} component="label">
                Upload Media
                <input
                  hidden
                  type="file"
                  accept=".png,.jpg,.jpeg,.gif,.svg,.pdf,.doc,.docx,.wav,.mp3,.aac"
                  onChange={handleFile}
                />
              </Button>
              {mediaFile && <Chip label={mediaFile.name} variant="outlined" />}
              {!mediaFile && imageUrl && (
                <Chip label="Using existing media" color="success" variant="outlined" />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Accepted: PNG, JPG, JPEG, GIF, SVG, PDF, DOC, DOCX, WAV, MP3, AAC — max 10MB
            </Typography>
          </Box>
        </Stack>

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={submit} variant="contained" disableElevation>
            {isEditMode ? 'Update News' : 'Create News'}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default AddNews;
